'use client';

import type { LangCode } from '@/lib/lang';
import { getManifest, speechManifests, supportsLanguage } from './manifest';
import type { ProviderId, SpeechKind, SttProviderClient, TtsProviderClient } from './types';

export const DEFAULT_TTS_PROVIDER_ID: ProviderId = 'cambai';
export const DEFAULT_STT_PROVIDER_ID: ProviderId = 'cambai';

/**
 * Client half of the registry. Factories are lazy so a provider's code is only
 * downloaded when it is actually selected — adding a tenth vendor costs nothing
 * in the initial bundle.
 *
 * Adding a provider: one line in each map plus providers/<id>/client.{tts,stt}.ts.
 */
const ttsFactories: Factories<TtsProviderClient> = {
  cambai: async () => (await import('./providers/cambai/client.tts')).createCambTts(),
  iflytek: async () => (await import('./providers/iflytek/client.tts')).createIflytekTts(),
  qwen: async () => (await import('./providers/qwen/client.tts')).createQwenTts(),
};

const sttFactories: Factories<SttProviderClient> = {
  cambai: async () => (await import('./providers/cambai/client.stt')).createCambStt(),
  iflytek: async () => (await import('./providers/iflytek/client.stt')).createIflytekStt(),
  qwen: async () => (await import('./providers/qwen/client.stt')).createQwenStt(),
  platformai: async () => (await import('./providers/platformai/client.stt')).createPlatformAiStt(),
};

export interface ResolvedProvider<T> {
  providerId: ProviderId;
  /** set when `providerId` is not what the caller asked for */
  fallbackFrom?: ProviderId;
  load(): Promise<T>;
}

type Factories<T> = Record<ProviderId, (() => Promise<T>) | undefined>;

function resolve<T>(
  kind: SpeechKind,
  factories: Factories<T>,
  requestedId: ProviderId,
  defaultId: ProviderId,
  language: LangCode,
): ResolvedProvider<T> {
  const usable = (id: ProviderId) => Boolean(factories[id]) && supportsLanguage(id, kind, language);

  // 1. honour the request, 2. fall back to the default, 3. any provider that can
  // actually serve this language. Silent degradation is the thing we are trying
  // to avoid, so anything other than (1) is reported as fallbackFrom.
  const candidates = [requestedId, defaultId, ...speechManifests.map((m) => m.id)];
  const pick = candidates.find(usable);
  const picked = pick ? factories[pick] : undefined;

  if (pick && picked) {
    return {
      providerId: pick,
      fallbackFrom: pick === requestedId ? undefined : requestedId,
      load: picked,
    };
  }

  // Nothing declares support for this language. Try the request anyway so the
  // failure surfaces as a real vendor error rather than the UI doing nothing.
  const last = factories[requestedId] ? requestedId : defaultId;
  const factory = factories[last];
  return {
    providerId: last,
    fallbackFrom: last === requestedId ? undefined : requestedId,
    load: factory
      ? factory
      : async () => {
          throw new Error(
            `No ${kind} implementation registered for "${requestedId}"` +
            `${getManifest(requestedId) ? '' : ' (unknown provider)'}`,
          );
        },
  };
}

export function resolveTtsProvider(requestedId: ProviderId, language: LangCode) {
  return resolve<TtsProviderClient>('tts', ttsFactories, requestedId, DEFAULT_TTS_PROVIDER_ID, language);
}

export function resolveSttProvider(requestedId: ProviderId, language: LangCode) {
  return resolve<SttProviderClient>('stt', sttFactories, requestedId, DEFAULT_STT_PROVIDER_ID, language);
}

/** Provider ids that actually have a client implementation for this capability. */
export function implementedProviderIds(kind: SpeechKind): ProviderId[] {
  return Object.keys(kind === 'tts' ? ttsFactories : sttFactories);
}
